import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  PersonAdd,
  Work,
  CardMembership,
  Assignment,
  School,
  Psychology,
  Info,
} from '@mui/icons-material';
import type { RecentActivity } from '../../services/superAdminService';

interface RecentActivityProps {
  activities: RecentActivity[];
}

const RecentActivity: React.FC<RecentActivityProps> = ({ activities }) => {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registered':
        return <PersonAdd />;
      case 'job_posted':
        return <Work />;
      case 'certificate_issued':
        return <CardMembership />;
      case 'application_submitted':
        return <Assignment />;
      case 'course_created':
        return <School />;
      case 'test_completed':
        return <Psychology />;
      default:
        return <Info />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'user_registered':
        return 'success.main';
      case 'job_posted':
        return 'info.main';
      case 'certificate_issued':
        return 'warning.main';
      case 'application_submitted':
        return 'primary.main';
      case 'course_created':
        return 'secondary.main';
      case 'test_completed':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - activityTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <List>
          {Array.isArray(activities) && activities.map((activity, index) => (
            <ListItem key={activity.id || `activity-${index}`}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: getActivityColor(activity.type) }}>
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={activity.title}
                secondary={`${activity.description} - ${getTimeAgo(activity.timestamp)}`}
              />
            </ListItem>
          ))}
          {(!Array.isArray(activities) || activities.length === 0) && (
            <ListItem>
              <ListItemText
                primary="No recent activity"
                secondary="Activity will appear here as users interact with the system"
              />
            </ListItem>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default RecentActivity;
